import type { WeldTorch } from '../weldTxtParser'

/** Separator between torch and raw zone name in mapping keys (`Lead:HB`). */
export const TORCH_PASS_KEY_SEP = ':'

export interface PassMappingRow {
  /** Stable mapping key, e.g. `Lead:HB` or plain `HB` when torch is unknown. */
  key: string
  passRaw: string
  torch?: WeldTorch
}

export function buildTorchPassKey(torch: WeldTorch, passRaw: string): string {
  return `${torch}${TORCH_PASS_KEY_SEP}${passRaw}`
}

export function passRawFromMappingKey(key: string): string {
  const idx = key.indexOf(TORCH_PASS_KEY_SEP)
  if (idx === -1) return key
  return key.slice(idx + 1)
}

export function torchFromMappingKey(key: string): WeldTorch | undefined {
  const idx = key.indexOf(TORCH_PASS_KEY_SEP)
  if (idx === -1) return undefined
  const torch = key.slice(0, idx)
  return torch === 'Lead' || torch === 'Trail' ? torch : undefined
}

/** Safe test-id fragment — colons are awkward in query selectors. */
export function passMappingKeyToTestId(key: string): string {
  return key.replace(/:/g, '-')
}

export function passMappingRowsFromSamples(
  samples: Array<{ passRaw: string | null; torch?: WeldTorch }>
): PassMappingRow[] {
  const seen = new Map<string, PassMappingRow>()
  for (const sample of samples) {
    if (!sample.passRaw) continue
    const torch = sample.torch
    const key =
      torch === 'Lead' || torch === 'Trail'
        ? buildTorchPassKey(torch, sample.passRaw)
        : sample.passRaw
    if (!seen.has(key)) {
      seen.set(key, {
        key,
        passRaw: sample.passRaw,
        torch: torch === 'Lead' || torch === 'Trail' ? torch : undefined
      })
    }
  }
  return Array.from(seen.values()).sort(comparePassMappingRows)
}

export function passMappingKeysFromRows(rows: PassMappingRow[]): string[] {
  return rows.map((row) => row.key)
}

export function passMappingRowsFromKeys(keys: string[]): PassMappingRow[] {
  const rows = keys.map((key) => ({
    key,
    passRaw: passRawFromMappingKey(key),
    torch: torchFromMappingKey(key)
  }))
  return rows.sort(comparePassMappingRows)
}

function comparePassMappingRows(a: PassMappingRow, b: PassMappingRow): number {
  const passCmp = a.passRaw.localeCompare(b.passRaw)
  if (passCmp !== 0) return passCmp
  const torchRank = (torch?: WeldTorch) => (torch === 'Lead' ? 0 : torch === 'Trail' ? 1 : 2)
  return torchRank(a.torch) - torchRank(b.torch)
}

/**
 * Resolve the canonical WPS label for a sample. Torch-specific keys win;
 * plain `passRaw` keys remain supported for ALX / legacy mappings.
 */
export function resolvePassMapping(
  passRaw: string | null | undefined,
  torch: WeldTorch | null | undefined,
  passMappings: Record<string, string>
): string | undefined {
  if (!passRaw) return undefined
  if (torch === 'Lead' || torch === 'Trail') {
    const torchKey = buildTorchPassKey(torch, passRaw)
    const torchMapped = (passMappings[torchKey] ?? '').trim()
    if (torchMapped) return torchMapped
  }
  const legacy = (passMappings[passRaw] ?? '').trim()
  return legacy || undefined
}

export function uniquePassRawsFromMappingKeys(keys: string[]): string[] {
  const seen = new Set<string>()
  for (const key of keys) {
    seen.add(passRawFromMappingKey(key))
  }
  return Array.from(seen).sort()
}
